import React, { createContext, useState, useEffect, useContext, ReactNode } from 'react';

// ==========================================
// 1. 定義型別 (Types)
// ==========================================
export interface User {
  id: number;
  email: string;
  nickname: string | null;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (token: string, user: User) => void;
  logout: () => void;
}

// ==========================================
// 2. 建立 Context
// ==========================================
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ==========================================
// 3. Provider 元件 (負責包裹整個 App 並管理狀態)
// ==========================================
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
    return localStorage.getItem('isLoggedIn') === 'true';
  });

  const [isInitializing, setIsInitializing] = useState(true);

  // 網頁載入與狀態變動時的生命週期監聽
  useEffect(() => {
    const storedExpiresAt = localStorage.getItem('tokenExpiresAt');
    const isExpired = storedExpiresAt ? Date.now() > parseInt(storedExpiresAt, 10) : false;

    if (token && user && isAuthenticated && !isExpired) {
      // 狀態完好且未過期，設定「自動登出計時器」
      // 若使用者持續開著網頁，時間一到就會自動執行登出並跳轉
      const timeLeft = parseInt(storedExpiresAt!, 10) - Date.now();
      const timeoutId = setTimeout(() => {
        logout();
        window.location.href = '/login'; // 強制導向登入頁
      }, timeLeft);

      setIsInitializing(false);
      return () => clearTimeout(timeoutId); // 元件卸載時清除計時器
    } else {
      // 安全重置：若資料有缺漏或已過期，乾淨清除所有相關欄位
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('tokenExpiresAt'); // 新增清除過期時間欄位
      
      setIsAuthenticated(false);
      setToken(null);
      setUser(null);
      setIsInitializing(false);
    }
  }, [token, user, isAuthenticated]);

  // 執行登入
  const login = (newToken: string, newUser: User) => {
    setToken(newToken);
    setUser(newUser);
    setIsAuthenticated(true);
    
    // 核心新增：解析 JWT Token 取得後端設定的精確到期時間 (exp)
    try {
      // JWT 格式為 header.payload.signature，取第二段 payload 進行 Base64 解碼
      const payload = JSON.parse(atob(newToken.split('.')[1]));
      const expiresAt = payload.exp * 1000; // JWT 的 exp 單位為秒，轉為毫秒
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());
    } catch (e) {
      // 備用防呆：若解析失敗，預設設定 24 小時後過期
      const expiresAt = Date.now() + 24 * 60 * 60 * 1000;
      localStorage.setItem('tokenExpiresAt', expiresAt.toString());
    }

    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('isLoggedIn', 'true'); 
    localStorage.setItem('userEmail', newUser.email);
  };

  // 執行登出
  const logout = () => {
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    
    // 清除所有欄位
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedIn'); 
    localStorage.removeItem('userEmail');
    localStorage.removeItem('tokenExpiresAt'); // 同步清除過期時間欄位
  };

  if (isInitializing) {
    return null; 
  }

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// ==========================================
// 4. Custom Hook (讓其他元件方便存取 AuthContext)
// ==========================================
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth 必須在 AuthProvider 內使用 (請確認 main.tsx 是否有正確包裹)');
  }
  return context;
};